import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { message, Button, Typography, Flex } from "antd";
import {
  useSearchParams,
  useLoaderData,
  useParams,
} from "react-router-dom";
import { useTenantNavigation } from "../../../hooks/useTenantNavigation.js";
import { useTranslation } from "react-i18next";
import { HomeOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "../../../axios-interceptor.js";
import EmailTabs from "./tabs.jsx";
import BreadCrumb from "../../BreadCrumb.js";
import { getCurrentSpaceData, getUserSpaceEntitlements } from "../../../utils/currentUserDetail.js";
import { getSpaceNotification } from "../../../services/notificationServices.js";
import { use } from "i18next";
import { showLoader } from "../../../utils/showloader.js";

export const notificationItems = {
  "global-emails": {
    label: "Global Emails",
    children: {
      // EMAIL_SENT_TO_COLLABORATORS: {
      //   title: "Email sent to collaborators",
      //   description: "Sent manaully by a user, from a document, if the user requests to 'notify' collaborators about a document (accessible from a documents, dropdown menu). Note: content can be changed manaully by a user.",
      // },
      // SUMMARY_FOR_EVERY_EVENT_OF_THE_DAY: {
      //   title: "Summary for every event of the day",
      //   description: "Sent automatically, every day, to users who have events on documents.",
      // },
      CHATROOM_NOTIFICATION: {
        title: "Chatroom notification",
        description:
          "Sent automatically when a new message has been posted to our chatroom, to the configured users.",
      },
      // CHATROOM_EXPORT_NOTIFICATION: {
      //   title: "Export confirmation",
      //   description: "Sent when export is finished.",
      // },
      DOCUMENT_ARCHIVED: {
        title: "Document archived",
        description: "Sent when document is archived.",
      },
      DOCUMENT_DELETED: {
        title: "Document deleted",
        description: "Sent when document is deleted.",
      },
      // PUBLIC_FORM: {
      //   title: "Public Form",
      //   description: "iu[+"
      // }
    },
    updateEntitlement: "Notifications_GlobalEmails_Update"
  },
  "workflow-emails": {
    label: "Workflow Emails",
    children: {
      WORKFLOW_STEP_NOTIFICATION: {
        title: "Workflow step notification",
        description: "Sent automatically to configured users to notify when a document enters a step.",
      },
      WORKFLOW_STEP_REFUSAL: {
        title: "Workflow step refusal",
        description: "Sent automatically to configured users to notify when a workflow step is refused on a document.",
      },
      WORKFLOW_STEP_WAITING: {
        title: "Workflow step waiting",
        description: "Sent automatically to validators when a document enters a step.",
      },
      WORKFLOW_STEP_WAITING_WITH_DUE_DATE: {
        title: "Workflow step waiting, with due date",
        description: "Sent automatically to validators when a document enters a step, if the step has a configured due date.",
      },
      WORKFLOW_END_NOTIFICATION: {
        title: "Workflow completion notification",
        description: "Sent automatically to configured users to notify when a workflow is ended."
      },
      WORKFLOW_STEP_SENT_BACK: {
        title: "Workflow step rejected and/or sent back",
        description: `Sent automatically to the 'validators of the step the document goes back to', or 'the author of the document' (when going back to draft version) when a step is refused.`
      },
    },
    updateEntitlement: "Notifications_WorkflowEmails_Update"
  },
  "notification-rule-emails": {
    label: "Notification Rule Emails",
    children: {
    },
    updateEntitlement: "Notifications_NotificationRuleEmails_Update"
  },
};

const NotificationCenterPage = () => {
  const { t } = useTranslation();
  const navigate = useTenantNavigation()
  const [notifications, setNotifications] = useState([])
  const [notificationsMap, setNotificationsMap] = useState({})
  // const [allowedNotificationItems, setAllowedNotifications] = useState({})
  const [searchParams, setSearchParams] = useSearchParams();
  const { spaceId } = useParams()
  let tabSelected = searchParams.get("tab") || "global-emails";
  const [activeTab, setActiveTab] = useState(tabSelected)
  const [allowedNotificationItems, setAllowedNotificationItems] = useState({})

  const { userInfo, userGroupSpaces } = useSelector((state) => state.user)
  const currentSpaceData = getCurrentSpaceData(spaceId, userInfo.spaceInfo, userGroupSpaces) || {}

  const spaceEntitlements = getUserSpaceEntitlements(spaceId, userInfo.userAccess, userGroupSpaces)

  // Sync activeTab with URL parameter
  useEffect(() => {
    setActiveTab(tabSelected)
  }, [tabSelected])

  const onChangeTab = (value) => {
    setSearchParams({ tab: value });
    setActiveTab(value)
  };
  const getNotificationDetails = useCallback(async (showLoad=true) => {
    try {
      if(showLoad) showLoader(true)
      
      let response = await getSpaceNotification(spaceId)
      showLoader(false)
      if (response.success && response.data) {
      let predefinedNotifications = response.data.result.filter(notification => notification.notificationType !== "alert")

        setNotifications(response.data.result)
        const notificationMap = predefinedNotifications.reduce(
          (acc, { notificationType, ...rest }) => {
            acc[notificationType] = { ...rest };
            return acc;
          },
          {}
        );
        setNotificationsMap(notificationMap)
      }
    } catch (e) {
      // message.error('Some error occured')
    }
  }, [spaceId])

  // Fetch notification details on mount
  useEffect(() => {
    getNotificationDetails()
  }, [])

  useEffect(() => {
    if (spaceEntitlements.length === 0 || Object.keys(notificationsMap).length === 0) {
      return
    }
    let notificationTypes = {}
    if (userInfo?.isAdmin || spaceEntitlements.includes("Notifications_GlobalEmails_View")) {
      notificationTypes["global-emails"] = { ...notificationItems["global-emails"], emailType: "global" }
    }
    if (userInfo?.isAdmin || spaceEntitlements.includes("Notifications_WorkflowEmails_View")) {
      notificationTypes["workflow-emails"] = { ...notificationItems["workflow-emails"], emailType: "workflow" }
    }
    if (userInfo?.isAdmin || spaceEntitlements.includes("Notifications_NotificationRuleEmails_View")) {
      let alertNotifications = notifications.filter(notification => notification.notificationType === "alert")
      const notificationMap = alertNotifications.reduce(
          (acc, notification) => {
            acc[notification.notificationId] = { ...notification };
            return acc;
          },
          {}
        );

      notificationTypes["notification-rule-emails"] = { ...notificationItems["notification-rule-emails"], emailType: "alert", children: { ...notificationMap } }
    }
    setAllowedNotificationItems(notificationTypes)

  }, [spaceEntitlements, notificationsMap])

  const addNewCustomAlert = () => {
    // Navigate to the alert creation page
    navigate(`/app/space/${spaceId}/notification-center/new?type=alert&notificationType=`);
  }

  const addNewDocGenerator = () => {
    navigate(`/app/space/${spaceId}/notification-center/new?type=alert&notificationType=`);

  }

  return (
    <div className="page-wrapper">
      <BreadCrumb
        items={[
          {
            title: <HomeOutlined />,
            path: userInfo.isAdmin ? "/app/dashboard" : "/app/dashboard",
          },
          {
            title: currentSpaceData?.name,
            path: `/app/space/${spaceId}/documents`
          },
          {
            title: t("Workflow"),
            path: `/app/space/${spaceId}/workflow`
          },
          {
            title: t("Notification Center"),
          }
        ]}
        marginb="24px"
      />

      <div>
        <Flex style={{ justifyContent: "space-between", alignItems: "center", margin: "20px 0 0px 0" }}>
          <Flex align="center" justify="space-between" style={{ display: "flex", background: "#eeeef0", width: "max-content", borderRadius: "0.375rem", padding: "0.25rem" }}>
            <div className="cursor-pointer widget-tab" style={{ background: activeTab === "global-emails" ? "#fff" : "#eeeef0" }} onClick={() => onChangeTab("global-emails")}>{t("Global Notification")}</div>
            <div className="cursor-pointer widget-tab" style={{ background: activeTab === "workflow-emails" ? "#fff" : "#eeeef0" }} onClick={() => onChangeTab("workflow-emails")}>{t("Workflow Notification")}</div>
            <div className="cursor-pointer widget-tab" style={{ background: activeTab === "notification-rule-emails" ? "#fff" : "#eeeef0" }} onClick={() => onChangeTab("notification-rule-emails")}>{t("Alert Notification")}</div>
            {/* <div className="cursor-pointer widget-tab" style={{ background: activeTab === "document-generator" ? "#fff" : "#eeeef0" }} onClick={() => onChangeTab("document-generator")}>{t("Document Generator")}</div> */}

          </Flex>
          {
            activeTab === "notification-rule-emails" && <Button style={{ borderColor: "#141BCB", color: "#141BCB", }} icon={<PlusOutlined />} onClick={addNewCustomAlert}>  {t("Create new alert")}</Button>
          }
          {
            activeTab === "document-generator" && <Button style={{ borderColor: "#141BCB", color: "#141BCB", }} icon={<PlusOutlined />} onClick={addNewDocGenerator}>{t("Create new document")}</Button>
          }
        </Flex>
        <div className="min-h-[30vh]">
          {
            Object.keys(allowedNotificationItems).length > 0 && allowedNotificationItems[activeTab] &&
            <>              
            <EmailTabs
              spaceEntitlements={spaceEntitlements}
              notifications={Object.keys(allowedNotificationItems[activeTab]?.children || {}).map(
                (key) => {
                  const childData = allowedNotificationItems[activeTab]?.children[key] || {};
                  const notificationData = notificationsMap[key] || {};
                  return {
                    entitlement: allowedNotificationItems[activeTab]?.updateEntitlement,
                    notificationType: key,
                    ...childData,
                    ...notificationData
                  };
                }
              )}
              emailType={allowedNotificationItems[activeTab]?.emailType}
              getNotificationDetails={getNotificationDetails}
              userInfo={userInfo}
            />
            </>
          }
        </div>
      </div>
    </div>
  );
};

export default NotificationCenterPage;

